import type { ResolveShareLinkResponse } from "@shared/types";
import { AlertCircle, ArrowRight, Loader2, Lock, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { resolveShareLink } from "../services/share-links-api.service";
import { getArtifactLabel } from "../services/share-message.service";

/**
 * Defensive: only allow in-app paths through programmatic navigation.
 * Server-supplied targetPath should always be a relative `/...` path, but a
 * future bug or breach in the resolver should not let `//evil.com/x` redirect
 * recipients off-domain (todo 018).
 */
function safeInternalPath(value: string | null | undefined): string | null {
	if (!value || typeof value !== "string") return null;
	if (!value.startsWith("/")) return null;
	if (value.startsWith("//")) return null; // protocol-relative escape
	return value;
}

export function SharedArtifactPage(): React.ReactElement {
	const { token } = useParams<{ token: string }>();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [result, setResult] = useState<ResolveShareLinkResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!token) return;
		let cancelled = false;
		setIsLoading(true);
		resolveShareLink(token)
			.then((data) => {
				if (cancelled) return;
				setResult(data);
				setError(null);
			})
			.catch(() => {
				if (cancelled) return;
				setResult(null);
				setError(t("sharing.unavailable"));
			})
			.finally(() => {
				if (cancelled) return;
				setIsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [token, t]);

	if (isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-dashboard-bg p-6">
				<Loader2 className="h-6 w-6 animate-spin text-dashboard-muted" />
			</div>
		);
	}

	const summary = result?.summary;
	const unavailable =
		!!error ||
		result?.accessState === "expired" ||
		result?.accessState === "revoked" ||
		result?.accessState === "unavailable";

	const safeTarget = safeInternalPath(result?.targetPath ?? null);

	const cardTitle = summary?.title
		? summary.title
		: unavailable
			? t("sharing.linkUnavailable")
			: t("sharing.sharedItem");

	const cardDescription = summary
		? summary.projectTitle
			? t("sharing.artifactSharedFor", {
					artifact: getArtifactLabel(summary.type),
					project: summary.projectTitle,
				})
			: t("sharing.artifactShared", {
					artifact: getArtifactLabel(summary.type),
				})
		: t("sharing.unavailable");

	return (
		<div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
			<Card className="w-full max-w-lg">
				<CardHeader>
					<div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
						{result?.accessState === "login_required" ? (
							<Lock className="h-5 w-5" />
						) : unavailable ? (
							<AlertCircle className="h-5 w-5" />
						) : (
							<Share2 className="h-5 w-5" />
						)}
					</div>
					<CardTitle>{cardTitle}</CardTitle>
					<CardDescription>{cardDescription}</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{result?.message && (
						<p className="text-sm text-muted-foreground">{result.message}</p>
					)}
					{result?.accessState === "forbidden" && (
						<>
							<p className="text-sm text-muted-foreground">
								{t("sharing.forbiddenHint")}
							</p>
							<Button asChild variant="outline" className="w-full">
								<Link to="/">{t("sharing.goToFinishd")}</Link>
							</Button>
						</>
					)}
					{result?.accessState === "login_required" && (
						<Button asChild className="w-full">
							<Link
								to={`/login?returnTo=${encodeURIComponent(
									window.location.pathname,
								)}`}
							>
								{t("sharing.signInToContinue")}
								<ArrowRight className="h-4 w-4" />
							</Link>
						</Button>
					)}
					{result?.accessState === "available" && safeTarget && (
						<Button className="w-full" onClick={() => navigate(safeTarget)}>
							{t("sharing.openInFinishd")}
							<ArrowRight className="h-4 w-4" />
						</Button>
					)}
					{unavailable && (
						<Button asChild variant="outline" className="w-full">
							<Link to="/login">{t("sharing.goToFinishd")}</Link>
						</Button>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
